import React, { useEffect, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  ListSubheader,
  MenuItem,
} from '@mui/material';

import {
  Menu as MenuIcon,
  DashboardOutlined as DashboardOutlinedIcon,
  HomeOutlined as HomeOutlinedIcon,
  TableViewOutlined as TableViewOutlinedIcon,
  GroupOutlined as GroupOutlinedIcon,
  GroupsOutlined as GroupsOutlinedIcon,
  MapsHomeWorkOutlined as MapsHomeWorkOutlinedIcon,
  PersonOutlineOutlined as PersonOutlineOutlinedIcon,
  ExitToAppOutlined as ExitToAppOutlinedIcon,
} from '@mui/icons-material';

import { usePopupState, bindTrigger, bindMenu } from 'material-ui-popup-state/hooks';

import { useDispatch } from 'react-redux';

import { useTypeSafeTranslation, changeLanguage } from '@/i18n';
import ConfirmDialog from '@/components/organisms/ConfirmDialog';
import { RootState, useSelector, actions, AppDispatch } from '@/store';

import { PopupMenu } from '@/components/atoms';
import { LanguageSelector } from '@/components/molecules';

type Props = {
  signOut: (data?: Record<string | number | symbol, unknown> | undefined) => void;
};

const GlobalHeader: React.FC<Props> = (props) => {
  // Redux State
  const dispatch = useDispatch<AppDispatch>();
  const language = useSelector<RootState>((state) => state.main.persistent.language) as string;
  const setLanguage = (lang: string) => dispatch(actions.setLanguage(lang));

  // ローカルState
  const navigate = useNavigate();
  const { t } = useTypeSafeTranslation();

  const [userEmail, setUserEmail] = useState('');

  const [naviMenuOpen, setNaviMenuOpen] = useState(false);
  const userMenu = usePopupState({ variant: 'popover', popupId: 'userMenu' });
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      const currentUserInfo = await Auth.currentUserInfo();
      setUserEmail(currentUserInfo.attributes.email);
    })();
  }, [language]);

  const signOut = () => {
    setDialogOpen(true);
  };

  const handleDialogConfirm = () => {
    setDialogOpen(false);
    props.signOut();
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
  };

  const handleLanguageChange = (lang: string) => {
    setLanguage(lang);
    changeLanguage(lang);
  };

  const menuItem = (path: string, text: string, icon: React.ReactElement) => (
    <ListItem disablePadding>
      <ListItemButton
        onClick={() => {
          navigate(path);
          setNaviMenuOpen(false);
        }}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={text} />
      </ListItemButton>
    </ListItem>
  );

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar position="fixed">
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => setNaviMenuOpen(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              <span
                style={{ cursor: 'pointer', fontWeight: 'bold' }}
                onClick={() => {
                  navigate('/');
                }}
              >
                {t('common.appName')}
              </span>
            </Typography>

            <LanguageSelector value={language} languageCodeList={['ja', 'en']} onChange={handleLanguageChange} />

            <IconButton color="inherit" {...bindTrigger(userMenu)}>
              <PersonOutlineOutlinedIcon />
            </IconButton>
            <PopupMenu align="right" {...bindMenu(userMenu)}>
              <Typography sx={{mx: 2, my: 1}}>{userEmail}</Typography>
              <Divider sx={{my: 1}}/>
              <MenuItem>
                <PersonOutlineOutlinedIcon />
                &nbsp;{t('common.profile')}
              </MenuItem>
              <MenuItem onClick={signOut}>
                <ExitToAppOutlinedIcon />
                &nbsp;{t('common.signOut')}
              </MenuItem>
            </PopupMenu>
          </Toolbar>
        </AppBar>
      </Box>

      <Drawer  anchor="left" open={naviMenuOpen} onClose={() => setNaviMenuOpen(false)}>
        <List sx={{ width: 250 }}>
          <ListSubheader component="div">{t('common.mainMenu')}</ListSubheader>
          {menuItem('/', t('pages.top.title'), <HomeOutlinedIcon />)}
          {menuItem('/feature1', 'Feature 1', <DashboardOutlinedIcon />)}
          <Divider />
          <ListSubheader component="div">{t('common.userAdminMenu')}</ListSubheader>
          {menuItem('/feature2', 'Feature 2', <TableViewOutlinedIcon />)}
          {menuItem('/feature3', 'Feature 3', <GroupOutlinedIcon />)}
          {menuItem('/feature4', 'Feature 4', <GroupsOutlinedIcon />)}
          <ListSubheader component="div">{t('common.sysAdminMenu')}</ListSubheader>
          {menuItem('/feature5', 'Feature 5', <MapsHomeWorkOutlinedIcon />)}
        </List>
      </Drawer>
      <ConfirmDialog
        open={dialogOpen}
        cancelable
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
      >
        {t('common.msg.signOutConfirm')}
      </ConfirmDialog>
    </>
  );
};

export default GlobalHeader;
